import { useEffect, useMemo, useRef } from 'react'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import { getSmallAssetPath } from '@aytm/lib/common/assets'
import { unescapeTags } from '@aytm/lib/common/func'
import {
  EcommerceBannerPlacement,
  EcommerceResponse,
  IEcommerceAnswerSchema,
  IEcommerceQuestionSchema
} from '../../types.ts'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale.ts'
import AddToCartButton from './AddToCartButton.tsx'
import BannerCard from './BannerCard.tsx'
import style from './AddedToCartModal.module.scss'

interface IAddedToCartModalProps {
  schema: IEcommerceQuestionSchema
  answer: IEcommerceAnswerSchema
  response: EcommerceResponse
  findability: boolean
  onItemClose: () => void
  onItemSelect: (index: number, bannerPlacement?: EcommerceBannerPlacement) => void
  onCartOpen: () => void
  onAddToCart: (index: number, qnt: number, inCarousel?: boolean) => void
  onModalClose: () => void
}

// Mobile "Added to cart!" sheet shown after the first add from the product page: the added
// line with its stepper, a "Members also bought" carousel and a View cart control.
const AddedToCartModal = ({
  schema,
  answer,
  response,
  findability,
  onItemClose,
  onItemSelect,
  onCartOpen,
  onAddToCart,
  onModalClose
}: IAddedToCartModalProps) => {
  const { t, languageTag } = useSurveyLocale()
  const dialogRef = useRef<HTMLDialogElement>(null)

  const amount = response.answers[answer.index].v
  const hasDiscount = Boolean(answer.discounted_price) && answer.price > answer.discounted_price
  const price = (value: number) => formatPriceWithLocale(value, schema.price_mask, languageTag, true, false)
  const cartCount = schema.answers.reduce((sum, item) => (item.na ? sum : sum + response.answers[item.index].v), 0)
  // Same pool as the product page's "Members also considered": featured top-level products, minus this one.
  const alsoBought = useMemo(
    () => schema.answers.filter(item => item.featured && !item.na && !item.parent_product && item.id !== answer.id),
    [schema.answers, answer]
  )

  // Stepping the line down to zero un-adds it — nothing left to confirm.
  useEffect(() => {
    if (amount <= 0) onModalClose()
  }, [amount, onModalClose])

  const onViewCart = () => {
    onModalClose()
    onCartOpen()
  }

  return (
    <dialog
      open
      ref={dialogRef}
      className={style.layout}
      data-testid="SW-EC-AddedToCartModal"
      onClick={event => {
        // A click on the ::backdrop targets the dialog element itself.
        if (event.target === dialogRef.current) onModalClose()
      }}
    >
      <div className={style.sheet}>
        <div className={style.head}>
          <span className={style.headTitle}>
            <i className={`ld-CheckCircle ${style.headIcon}`} />
            {t('questions', 'ec', 'samsclub', 'addedToCart')}
          </span>
          <button
            type="button"
            className={style.close}
            onClick={onModalClose}
            aria-label={t('questions', 'ec', 'samsclub', 'close')}
          >
            <i className="ld-Close" />
          </button>
        </div>

        <div className={style.body}>
          <div className={style.product}>
            {answer.image ? (
              <img
                className={style.productImage}
                alt={answer.image.alt_text || t('questions', 'img', 'noAlt')}
                src={getSmallAssetPath(answer.image)}
              />
            ) : (
              <div className={style.productNoImage} />
            )}
            <div className={style.productInfo}>
              <div className={style.productTitle} dangerouslySetInnerHTML={{ __html: answer.text }} />
              <div className={style.priceRow}>
                {hasDiscount ? (
                  <>
                    <span
                      className={style.priceSale}
                      dangerouslySetInnerHTML={{ __html: price(answer.discounted_price) }}
                    />
                    <span className={style.priceOld} dangerouslySetInnerHTML={{ __html: price(answer.price) }} />
                  </>
                ) : (
                  <span className={style.price} dangerouslySetInnerHTML={{ __html: price(answer.price) }} />
                )}
              </div>
              {answer.per_count_price && (
                <div
                  className={style.unitPrice}
                  dangerouslySetInnerHTML={{ __html: unescapeTags(answer.per_count_price) }}
                />
              )}
              <div className={style.stepper}>
                <AddToCartButton
                  amount={amount}
                  findability={findability}
                  limit={schema.max_qty_per_product}
                  placement="banner"
                  onAdd={qnt => onAddToCart(answer.index, qnt)}
                />
              </div>
            </div>
          </div>

          {alsoBought.length > 0 && (
            <div className={style.alsoBought}>
              <h2 className={style.sectionTitle}>{t('questions', 'ec', 'samsclub', 'membersAlsoBought')}</h2>
              <div className={style.carousel} data-testid="SW-EC-MembersAlsoBought">
                {alsoBought.map(item => (
                  <div className={style.carouselItem} key={item.id}>
                    <BannerCard
                      schema={schema}
                      answer={item}
                      amount={response.answers[item.index].v}
                      findability={findability}
                      disabled={'focus' == schema.mode_type && !item.focused}
                      variant="recommendation"
                      onSelect={() => {
                        onModalClose()
                        onItemClose()
                        onItemSelect(item.index, 'featured')
                      }}
                      onAdd={qnt => onAddToCart(item.index, qnt, true)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={style.foot}>
          <button type="button" className={style.viewCart} onClick={onViewCart}>
            {t<(count: number) => string>('questions', 'ec', 'samsclub', 'viewCart')(cartCount)}
          </button>
        </div>
      </div>
    </dialog>
  )
}

export default AddedToCartModal
